"use client";

import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  InputLabel,
  ListItemText,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Validation } from "./validation";
import { getTheme } from "../../styles/theme";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
// CommonForm.js
export default function CommonForm({
  formValues,
  setFormValues,
  handleChange,
  availableRooms,
  setAvailableRooms,
  availableRoomsRef,
  setLoader,
  initialState,
  setSelectedRooms,
  setOrderType,
  setAllRooms,
  roomControl,
  setRoomControl,
  setSeeAvailableRoomNumber,
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [checkInError, setCheckInError] = React.useState(false);
  const [checkOutError, setCheckOutError] = React.useState(false);

  const [checkIn, setCheckIn] = [
    formValues.checkIn,
    (date) => handleChange({ target: { name: "checkIn", value: dayjs(date) } }),
  ];
  const [checkOut, setCheckOut] = [
    formValues.checkOut,
    (date) =>
      handleChange({ target: { name: "checkOut", value: dayjs(date) } }),
  ];

  // Duration is derived from the dates (complete weeks, like the backend)
  // and editing it moves the check-out date.
  const durationWeeks =
    checkIn && checkOut && dayjs(checkOut).isAfter(checkIn)
      ? Math.floor(dayjs(checkOut).diff(dayjs(checkIn), "day") / 7)
      : "";

  const handleDurationChange = (event) => {
    const weeks = Number.parseInt(event.target.value, 10);
    handleChange({
      target: {
        name: "checkOut",
        value: weeks > 0 ? dayjs(checkIn).add(weeks, "week") : null,
      },
    });
  };

  const [providers, setProviders] = React.useState([]);
  const [campuses, setCampuses] = React.useState([]);
  const [universities, setUniversities] = React.useState([]);
  const [courses, setCourses] = React.useState([]);

  // Stable primitive IDs for useEffect deps - prevents object ref changes from triggering re-fetches
  const normalizeToArray = (val) =>
    Array.isArray(val) ? val : val ? [val] : [];
  const providerIds = React.useMemo(
    () =>
      normalizeToArray(formValues?.provider)
        .map((p) => p.partner_id)
        .join(","),
    [formValues?.provider]
  );
  const universityIds = React.useMemo(
    () =>
      normalizeToArray(formValues?.universities)
        .map((u) => u.university_id)
        .join(","),
    [formValues?.universities]
  );
  const residenceIds = React.useMemo(
    () =>
      normalizeToArray(formValues?.residences)
        .map((r) => r.campus_id)
        .join(","),
    [formValues?.residences]
  );

  async function getProviders(universityIds) {
    const accessToken = localStorage.getItem("accessToken");
    const token = localStorage.getItem("loginUser");
    try {
      const params = new URLSearchParams();
      if (universityIds) params.append("universities", universityIds);

      const url = `${process.env.REACT_APP_BASE_URL}booking/providers`;
      const response = await axios.post(url, params, {
        headers: {
          Authorization: `Bearer ${accessToken}`, // Bearer token burada ekleniyor
          XRefreshToken: `${token}`, // Bearer token burada ekleniyor
          "Content-Type": "application/x-www-form-urlencoded", // Eğer gönderdiğiniz veri JSON formatındaysa bu başlık gerekli
        },
      });

      // İstek başarılı olduğunda sunucudan dönen yanıtı göster
      setProviders(response.data.result);
    } catch (error) {
      // Hata durumunda hata mesajını göster
      console.error("Error during the request:", error.message);
    }
  }

  async function getCampuses(partnerIds, universityIds) {
    try {
      const formData = new FormData();
      formData.append("partners", partnerIds || "");
      if (universityIds) formData.append("universities", universityIds);

      const accessToken = localStorage.getItem("accessToken");
      const token = localStorage.getItem("loginUser");
      const url = `${process.env.REACT_APP_BASE_URL}booking/campuses`;

      // Axios ile POST isteği yapılıyor
      const response = await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`, // Bearer token burada ekleniyor
          XRefreshToken: `${token}`, // Bearer token burada ekleniyor
          "Content-Type": "application/x-www-form-urlencoded", // Eğer gönderdiğiniz veri JSON formatındaysa bu başlık gerekli
        },
      });

      // İstek başarılı olduğunda sunucudan dönen yanıtı göster
      setCampuses(response.data.result);
    } catch (error) {
      // Hata durumunda hata mesajını göster
      console.error("Error during the request:", error.message);
    }
  }

  async function getCourses(url, apiKey, campusId, partnerIds, residenceIds) {
    try {
      // URLSearchParams ile POST parametreleri oluşturuluyor
      const params = new URLSearchParams();
      params.append("partners", partnerIds || "");
      params.append("residences", residenceIds || "");

      const accessToken = localStorage.getItem("accessToken");
      const token = localStorage.getItem("loginUser");
      const url = `${process.env.REACT_APP_BASE_URL}booking/room_types`;

      // Axios ile POST isteği yapılıyor
      const response = await axios.post(url, params, {
        headers: {
          Authorization: `Bearer ${accessToken}`, // Bearer token burada ekleniyor
          XRefreshToken: `${token}`, // Bearer token burada ekleniyor
          "Content-Type": "application/x-www-form-urlencoded", // Eğer gönderdiğiniz veri JSON formatındaysa bu başlık gerekli
        },
      });

      // İstek başarılı olduğunda sunucudan dönen yanıtı göster
      setCourses(response.data.result);
    } catch (error) {
      // Hata durumunda hata mesajını göster
      console.error("Error during the request:", error.message);
    }
  }

  async function getUniversities(campusIds, partnerIds) {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const token = localStorage.getItem("loginUser");
      const params = {};
      if (campusIds) params.campus_id = campusIds;
      if (partnerIds) params.partner_id = partnerIds;
      const url = `${process.env.REACT_APP_BASE_URL}universities`;

      const response = await axios.get(url, {
        params: Object.keys(params).length ? params : undefined,
        headers: {
          Authorization: `Bearer ${accessToken}`, // Bearer token burada ekleniyor
          XRefreshToken: `${token}`, // Bearer token burada ekleniyor
          "Content-Type": "application/x-www-form-urlencoded", // Eğer gönderdiğiniz veri JSON formatındaysa bu başlık gerekli
        },
      });

      // İstek başarılı olduğunda sunucudan dönen yanıtı göster
      setUniversities(response.data.universities);
    } catch (error) {
      // Hata durumunda hata mesajını göster
      console.error("Error during the request:", error.message);
    }
  }

  React.useEffect(() => {
    const url = process.env.WEBSERVICE_LONDONIST;
    const apiKey = process.env.WEBSERVICE_LONDONIST_APIKEY;
    getProviders();
    getCampuses();
    getUniversities();
    getCourses(url, apiKey, null, undefined, undefined);

    const urlParams = new URLSearchParams(window.location.search);
    if (formValues.refreshControl && urlParams.toString()) {
      navigate("/room-finder", { replace: true });
      return;
    }

    if (formValues.refreshControl) {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      const newFormValues = { ...formValues }; // Mevcut form değerlerinin bir kopyasını oluşturun

      for (const key in formValues) {
        if (urlParams.has(key)) {
          if (key === "checkIn" || key === "checkOut") {
            // checkIn ve checkOut için yeni değerleri hazırlayın
            newFormValues[key] = dayjs(urlParams.get(key));
          }
        }
      }

      // Tek bir setFormValues çağrısı ile tüm değişiklikleri uygulayın
      setFormValues(newFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (providers.length) {
      // URL'deki sorgu dizesini almak için
      const queryString = window.location.search;

      // URLSearchParams objesini kullanarak sorgu dizesini ayrıştırın
      const urlParams = new URLSearchParams(queryString);

      if (urlParams.size) {
        // Provider parametresini kontrol et
        if (urlParams.has("provider")) {
          const providerParam = urlParams.get("provider");

          // Virgülle ayrılmış provider ID'lerini ayır
          if (providerParam) {
            const providerIds = providerParam
              .split(",")
              .map((id) => Number.parseInt(id, 10));

            // Eğer birden fazla provider ID'si varsa
            if (providerIds.length > 1) {
              // Tüm provider ID'lerine karşılık gelen provider objelerini bul
              const selectedProviders = providers.filter((provider) =>
                providerIds.includes(provider.partner_id)
              );

              // formValues'ı güncelle
              if (selectedProviders.length > 0) {
                setFormValues((prev) => ({
                  ...prev,
                  provider: selectedProviders,
                }));
              }
            } else {
              // Tek bir provider ID'si varsa
              const findedProvider = providers.find(
                (provider) => provider.partner_id === providerIds[0]
              );

              // formValues'ı güncelle
              if (findedProvider) {
                setFormValues((prev) => ({
                  ...prev,
                  provider: findedProvider,
                }));
              }
            }
          }
        }

        // Diğer parametreleri işle
        for (const key in formValues) {
          if (urlParams.has(key) && key !== "provider") {
            // Provider dışındaki parametreleri işle
            // Burada diğer parametreler için gerekli işlemleri yapabilirsiniz
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers]);

  React.useEffect(() => {
    if (campuses.length) {
      const queryString = window.location.search;

      // URLSearchParams objesini kullanarak sorgu dizesini ayrıştırın
      const urlParams = new URLSearchParams(queryString);

      if (urlParams.size) {
        // Campus parametresini kontrol et
        if (urlParams.has("residences")) {
          const campusParam = urlParams.get("residences");

          // Virgülle ayrılmış campus ID'lerini ayır
          if (campusParam) {
            const campusIds = campusParam
              .split(",")
              .map((id) => Number.parseInt(id, 10));

            // Eğer birden fazla campus ID'si varsa
            if (campusIds.length > 1) {
              // Tüm campus ID'lerine karşılık gelen campus objelerini bul
              const selectedCampuses = campuses.filter((campus) =>
                campusIds.includes(campus.campus_id)
              );

              // formValues'ı güncelle
              if (selectedCampuses.length > 0) {
                setFormValues((prev) => ({
                  ...prev,
                  residences: selectedCampuses,
                }));
              }
            } else {
              // Tek bir campus ID'si varsa
              const findedCampus = campuses.find(
                (campus) => campus.campus_id === campusIds[0]
              );

              // formValues'ı güncelle
              if (findedCampus) {
                setFormValues((prev) => ({
                  ...prev,
                  residences: findedCampus,
                }));
              }
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campuses]);

  React.useEffect(() => {
    const queryString = window.location.search;

    // URLSearchParams objesini kullanarak sorgu dizesini ayrıştırın
    const urlParams = new URLSearchParams(queryString);

    if (urlParams.size) {
      // formValues objesindeki her anahtarı kontrol edin
      const findedCourse = courses.find((course) => {
        return (
          course.course_id === Number.parseInt(urlParams.get("course"), 10)
        );
      });

      if (findedCourse) {
        setFormValues((prev) => ({
          ...prev,
          course: findedCourse,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  React.useEffect(() => {
    if (universities.length) {
      const queryString = window.location.search;

      // URLSearchParams objesini kullanarak sorgu dizesini ayrıştırın
      const urlParams = new URLSearchParams(queryString);

      if (urlParams.size) {
        // Campus parametresini kontrol et
        if (urlParams.has("universities")) {
          const universityParam = urlParams.get("universities");

          if (universityParam) {
            const universityIds = universityParam
              .split(",")
              .map((id) => Number.parseInt(id, 10));

            if (universityIds.length > 1) {
              const selectedUniversities = universities.filter((university) =>
                universityIds.includes(university.university_id)
              );

              if (selectedUniversities.length > 0) {
                setFormValues((prev) => ({
                  ...prev,
                  universities: selectedUniversities,
                }));
              }
            } else {
              // Tek bir university ID'si varsa
              const findedUniversity = universities.find(
                (university) => university.university_id === universityIds[0]
              );

              // formValues'ı güncelle
              if (findedUniversity) {
                setFormValues((prev) => ({
                  ...prev,
                  universities: findedUniversity,
                }));
              }
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universities]);

  const handleFindRooms = async () => {
    if (
      formValues.checkIn &&
      formValues.checkOut &&
      formValues.courses !== "All Room Types"
    ) {
      const queryParts = [];
      const excludeFromUrl = [
        "currency",
        "numberOfStudents",
        "occupancy",
        "additionalNote",
      ];

      for (const [key, value] of Object.entries(formValues)) {
        // if (value && value !== "") {
        if (value && value !== "" && !excludeFromUrl.includes(key)) {
          if (key === "checkIn" || key === "checkOut") {
            // Eğer checkIn veya checkOut değerlerini işliyorsak, bu değerleri formatlamamız gerekiyor.
            queryParts.push(`${key}=${value.format("YYYY-MM-DD")}`);
          } else if (key === "provider") {
            // Provider değerinin tipini kontrol et
            if (Array.isArray(value) && value.length > 0) {
              // Birden fazla provider seçilmişse
              const providerIds = value.map((val) => val.partner_id).join(",");
              queryParts.push(`${key}=${providerIds}`);
            } else if (
              value &&
              typeof value === "object" &&
              "partner_id" in value
            ) {
              // Tek bir provider seçilmişse
              queryParts.push(`${key}=${value.partner_id}`);
            }
          } else if (key === "residences") {
            if (Array.isArray(value) && value.length > 0) {
              const campusIds = value.map((val) => val.campus_id).join(",");
              queryParts.push(`${key}=${campusIds}`);
            } else if (
              value &&
              typeof value === "object" &&
              "campus_id" in value
            ) {
              queryParts.push(`${key}=${value.campus_id}`);
            }
          } else if (key === "course") {
            if (value !== "All Room Types") {
              queryParts.push(`${key}=${value.course_id}`);
            }
          } else if (key === "universities") {
            if (Array.isArray(value) && value.length > 0) {
              const universityIds = value
                .map((val) => val.university_id)
                .join(",");
              queryParts.push(`${key}=${universityIds}`);
            } else if (
              value &&
              typeof value === "object" &&
              "university_id" in value
            ) {
              queryParts.push(`${key}=${value.university_id}`);
            }
          } else {
            queryParts.push(`${key}=${value}`);
          }
        }
      }

      const queryString = queryParts.join("&");
      navigate(`/room-finder?${queryString}`);
      try {
        setLoader(true);

        // URLSearchParams ile POST parametreleri oluşturuluyor
        const params = new URLSearchParams();

        if (formValues.provider) {
          // Provider değerinin tipini kontrol et
          if (
            Array.isArray(formValues.provider) &&
            formValues.provider.length > 0
          ) {
            // Birden fazla provider seçilmişse, partner_id'leri virgülle ayırarak ekle
            const providerIds = formValues.provider
              .map((provider) => provider.partner_id)
              .join(",");
            params.append("provider", providerIds);
          } else if (
            formValues.provider &&
            typeof formValues.provider === "object" &&
            "partner_id" in formValues.provider
          ) {
            // Tek bir provider seçilmişse
            params.append("provider", formValues.provider.partner_id);
          }
          // Eğer provider null veya undefined ise, hiçbir şey ekleme
        }

        if (formValues.residences) {
          if (
            Array.isArray(formValues.residences) &&
            formValues.residences.length > 0
          ) {
            const campusIds = formValues.residences
              .map((campus) => campus.campus_id)
              .join(",");
            params.append("residences", campusIds);
          } else if (
            formValues.residences &&
            typeof formValues.residences === "object" &&
            "partner_id" in formValues.residences
          ) {
            // Tek bir campus seçilmişse
            params.append("residences", formValues.residences.campus_id);
          }
          // Eğer provider null veya undefined ise, hiçbir şey ekleme
        }

        if (formValues.universities) {
          if (
            Array.isArray(formValues.universities) &&
            formValues.universities.length > 0
          ) {
            const universityId = formValues.universities
              .map((university) => university.university_id)
              .join(",");
            params.append("universities", universityId);
          } else if (
            formValues.universities &&
            typeof formValues.universities === "object" &&
            "university_id" in formValues.universities
          ) {
            // Tek bir university seçilmişse
            params.append(
              "universities",
              formValues.universities.university_id
            );
          }
          // Eğer provider null veya undefined ise, hiçbir şey ekleme
        }

        if (formValues.course) {
          if (
            !formValues.course.length &&
            formValues.course !== "All Room Types"
          ) {
            params.append("room_type", formValues.course.course_id);
          }
        }

        if (formValues.checkIn) {
          params.append("start_date", formValues.checkIn.format("YYYY-MM-DD"));
        }
        if (formValues.checkOut) {
          params.append("end_date", formValues.checkOut.format("YYYY-MM-DD"));
        }

        // TASK 3 FIX: Backend'e Occupancy bilgisini gönderiyoruz!
        if (formValues.occupancy && formValues.occupancy !== "") {
          // Eğer occupancy objeyse içindeki değeri, düz metinse metnin kendisini al
          const occupancyValue = typeof formValues.occupancy === "object"
            ? (formValues.occupancy.value || formValues.occupancy.occupancy_id)
            : formValues.occupancy;

          params.append("occupancy", occupancyValue);
        }

        const accessToken = localStorage.getItem("accessToken");
        const token = localStorage.getItem("loginUser");
        const url = `${process.env.REACT_APP_BASE_URL}booking/rooms`;

        // Axios ile POST isteği yapılıyor
        const response = await axios.post(url, params, {
          headers: {
            Authorization: `Bearer ${accessToken}`, // Bearer token burada ekleniyor
            XRefreshToken: `${token}`, // Bearer token burada ekleniyor
            "Content-Type": "application/x-www-form-urlencoded", // Eğer gönderdiğiniz veri JSON formatındaysa bu başlık gerekli
          },
          // headers: {
          //   "Content-Type": "application/x-www-form-urlencoded", // Form verisi olarak gönderiliyor
          // },
        });

        const resultRows = Array.isArray(response?.data?.result)
          ? response.data.result
          : [];

        const seeRoomNumbersRaw =
          response?.data?.see_available_room_number ??
          response?.data?.seeAvailableRoomNumber ??
          response?.data?.["see available room number"];
        setSeeAvailableRoomNumber?.(
          seeRoomNumbersRaw === undefined ? true : !!seeRoomNumbersRaw
        );

        const filteredAvailableRooms = resultRows.reduce(
          (acc, current) => {
            // Benzersiz anahtar oluşturma
            const uniqueKey = `${current.partner_name}_${current.campus_name}_${current.course_name}`;
            // Eğer bu anahtar daha önce eklenmemişse, yeni bir grup oluştur ve count'u 1 yap
            if (!acc.temp[uniqueKey]) {
              acc.temp[uniqueKey] = {
                ...current, // Mevcut oda bilgilerini al
                count: 1, // Bu gruptaki oda sayısını başlat
              };
            } else {
              // Eğer bu anahtar varsa, count'u artır
              acc.temp[uniqueKey].count += 1;
            }
            return acc;
          },
          { temp: {} }
        ).temp; // Reduce işlemi sonucunda temp objesini al

        // Sonuç olarak elde edilen objeyi diziye çevir
        const resultArray = Object.values(filteredAvailableRooms);

        // Bu dizi, her bir benzersiz oda grubu için bir obje içerir ve her objenin bir 'count' özelliği vardır.

        // Benzersiz objeleri setAvailableRooms ile kaydet

        // response'dan gelen veriyi işleme
        const availableRooms = resultRows;

        // Öğelerin tekrar sayısını tutacak bir nesne oluştur
        const itemCounts = {};

        // Her öğe için tekrar sayısını hesaplama
        availableRooms.forEach((room) => {
          const uniqueKey = `${room.partner_name}_${room.campus_name}_${room.course_name}`;
          if (itemCounts[uniqueKey]) {
            itemCounts[uniqueKey].count++;
            itemCounts[uniqueKey].items.push(room);
          } else {
            itemCounts[uniqueKey] = { count: 1, items: [room] };
          }
        });

        setOrderType("low-to-high");
        setAllRooms([]);
        setAvailableRooms(resultArray);
        setLoader(false);
        setRoomControl(false);
      } catch (error) {
        // Hata durumunda hata mesajını göster
        console.error("Error during the request:", error.message);
      }
    } else {
      Validation(
        formValues,
        checkInError,
        setCheckInError,
        checkOutError,
        setCheckOutError
      );
    }
  };

  React.useEffect(() => {
    console.log(formValues, "form values here");
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    if (
      urlParams.size &&
      Object.keys(formValues).length &&
      formValues.refreshControl
    ) {
      const filledFormKeys = [];

      for (const key in formValues) {
        if (
          key !== "refreshControl" &&
          formValues[key] !== null &&
          formValues[key] !== undefined &&
          formValues[key] !== ""
        ) {
          filledFormKeys.push(key);
        }
      }

      console.log(filledFormKeys, "filled form keys");

      // `refreshControl` parametresini URLSearchParams objesinden çıkar
      if (urlParams.has("refreshControl")) {
        urlParams.delete("refreshControl");
      }

      if (!availableRooms?.length) {
        // Burada, sorgu parametrelerinin sayısını kontrol ediyoruz
        if (Array.from(urlParams.keys()).length === filledFormKeys.length) {
          handleFindRooms();
        }
      }
    } else {
      Validation(
        formValues,
        checkInError,
        setCheckInError,
        checkOutError,
        setCheckOutError
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  React.useEffect(() => {
    const url = process.env.WEBSERVICE_LONDONIST;
    const apiKey = process.env.WEBSERVICE_LONDONIST_APIKEY;
    getCourses(url, apiKey, null, providerIds || undefined, residenceIds || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerIds, residenceIds]);

  React.useEffect(() => {
    getCampuses(providerIds || undefined, universityIds || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerIds, universityIds]);

  React.useEffect(() => {
    if (universityIds) {
      getProviders(universityIds);
    } else {
      getProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universityIds]);

  React.useEffect(() => {
    if (residenceIds) {
      getUniversities(residenceIds, undefined);
    } else if (providerIds) {
      getUniversities(undefined, providerIds);
    } else {
      getUniversities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residenceIds, providerIds]);

  React.useEffect(() => {
    if (!courses.length) {
      setFormValues((prev) => ({
        ...prev,
        course: "",
      }));
    } else {
      const selectedCourseExists = courses.some(
        (c) => JSON.stringify(c) === JSON.stringify(formValues.course)
      );

      if (!selectedCourseExists) {
        setFormValues((prev) => ({
          ...prev,
          course: "",
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  // residence reset kontrolü
  React.useEffect(() => {
    if (!campuses.length) {
      setFormValues((prev) => ({
        ...prev,
        residences: "",
      }));
    } else {
      const normalizeToArray = (val) =>
        Array.isArray(val) ? val : val ? [val] : [];

      const selectedResidences = normalizeToArray(formValues.residences);

      const filtered = selectedResidences.filter((res) =>
        campuses.some((c) => c.campus_id === res.campus_id)
      );

      if (filtered.length !== selectedResidences.length) {
        setFormValues((prev) => ({
          ...prev,
          residences: filtered.length ? filtered : "",
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campuses]);

  // provider reset when providers list refreshes
  React.useEffect(() => {
    if (!providers.length) {
      setFormValues((prev) => ({ ...prev, provider: "" }));
    } else {
      const normalizeToArray = (val) =>
        Array.isArray(val) ? val : val ? [val] : [];
      const selectedProviders = normalizeToArray(formValues?.provider);
      const filtered = selectedProviders.filter((p) =>
        providers.some((pr) => pr.partner_id === p.partner_id)
      );
      if (filtered.length !== selectedProviders.length) {
        setFormValues((prev) => ({
          ...prev,
          provider: filtered.length ? filtered : "",
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers]);

  // universities reset when universities list refreshes
  React.useEffect(() => {
    if (!universities.length) {
      setFormValues((prev) => ({ ...prev, universities: "" }));
    } else {
      const normalizeToArray = (val) =>
        Array.isArray(val) ? val : val ? [val] : [];
      const selectedUniversities = normalizeToArray(formValues?.universities);
      const filtered = selectedUniversities.filter((u) =>
        universities.some((un) => un.university_id === u.university_id)
      );
      if (filtered.length !== selectedUniversities.length) {
        setFormValues((prev) => ({
          ...prev,
          universities: filtered.length ? filtered : "",
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universities]);

  // React.useEffect(() => {
  //   if (formValues.provider) {
  //     let url = process.env.WEBSERVICE_LONDONIST;
  //     let apiKey = process.env.WEBSERVICE_LONDONIST_APIKEY;

  //     // let partnerIds = []
  //     // formValues.provider?.forEach((partner, idx) => {
  //     //   partnerIds?.push(partner?.partner_id);
  //     // });

  //     const providerIds = formValues.provider
  //       .map((provider) => provider.partner_id)
  //       .join(",");

  //     console.log(providerIds, "provider ids");
  //     getCampuses(url, apiKey, providerIds);

  //     if (!formValues.refreshControl) {
  //       setFormValues((prev) => ({
  //         ...prev,
  //         residences: "",
  //         // course: "",
  //       }));
  //     }
  //     setCourses([]);
  //   }
  // }, [formValues.provider]);

  const lightMode = useSelector((state) => state.mode.lightMode); // Redux state'inden lightMode değerini çekin
  // Redux üzerinden lightMode state'ini al

  // Ana temayı getir
  const baseTheme = getTheme(lightMode);

  // Lokal override'ları baseTheme'in üzerine uygula
  const theme = createTheme({
    ...baseTheme,
    components: {
      // MuiButton: {
      //   styleOverrides: {
      //     root: {
      //       backgroundColor: lightMode ? "#bf0000" : "#ffffff",
      //       color: lightMode ? "#ffffff" : "#000000",
      //       "&:hover": {
      //         backgroundColor: lightMode ? "#bf0000" : "#ffffff",
      //         color: lightMode ? "#ffffff" : "#000000",
      //       },
      //     },
      //   },
      // },
      MuiSelect: {
        styleOverrides: {
          root: {
            "&.MuiOutlinedInput-notchedOutline": {
              // top: -7
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              // borderColor: lightMode && "#1d1b1a !important",
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: "1rem",
          },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          paper: {
            borderRadius: "1rem",
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "#bf000038",
            },
            "&:not(.Mui-selected)": {
              // borderColor: "#bf000038 !important",
            },
            "&.Mui-selected": {
              backgroundColor: "#bf0000 !important",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#23416c !important",
              },
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            // top: 0,
            borderColor: !lightMode ? "#ffffff50" : "#ffffff50 !important",
          },

          input: {
            color: !lightMode && "#ffffff",
          },
          root: {
            color: "#fff",
            borderRadius: "1rem",
            backgroundColor: "#00000030", // ✅ input alanı arka planı her zaman beyaz
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: "#ffffff30 !important",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: !lightMode && "white",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: !lightMode && "white",
            },
          },
        },
      },

      MuiSvgIcon: {
        styleOverrides: {
          root: {
            "&.MuiSelect-icon": {
              color: "#fff",
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: !lightMode && "#ffffff",
            "&.MuiInputLabel-root.Mui-disabled": {
              color: "#ffffff50",
            },
            "&.MuiInputLabel-root": {
              // display: "none",
              // top: -7,
              color: "#fff",
            },
            "&.MuiInputLabel-root.Mui-focused": {
              // display: "none",
              color: !lightMode ? "#ffffff !important" : "#fff !important",
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            "&.check-in": {
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: checkInError && "#bf0000",
              },
              ".Mui-focused": {
                color: checkInError && "#bf0000",
              },
            },
            "&.check-out": {
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: checkOutError && "#bf0000",
              },
              ".Mui-focused": {
                color: checkOutError && "#bf0000",
              },
            },
          },
        },
      },
    },
  });

  const disableWeekdays = (date) => {
    // 6: Cumartesi, 0: Pazar
    const day = dayjs(date).day();
    return day !== 0 && day !== 6;
  };

  const disableSameDate = (date) => {
    if (formValues.checkIn && date.isSame(formValues.checkIn, "day")) {
      return true;
    }
    if (formValues.checkOut && date.isSame(formValues.checkOut, "day")) {
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    dayjs.locale(i18n.language); // aktif dili dayjs'e uygula
  }, [i18n.language]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: { xs: "auto", lg: "auto" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Box
          sx={{
            background: lightMode
              ? // theme.palette.primary.light
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.extraLight} 35%, ${theme.palette.primary.light} 100%)`
              : "#292929", // Koyu mavi arka plan
            width: "100%",
            height: "auto",
            minHeight: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            zIndex: 0,
            overflow: "hidden",
            borderRadius: "8px",
            padding: "20px",
          }}
        >
          <Box
            className="room-finder-form"
            sx={{
              zIndex: 2,
              width: "100%",
              maxWidth: "800px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingBottom: "1rem",
              svg: {
                color: "#fff",
              },
            }}
          >
            <LocalizationProvider
              /*             dateAdapter={AdapterDayjs}
                          adapterLocale={i18n.language} // dayjs locale olarak geçiriyoruz */
              dateAdapter={AdapterDayjs}
              adapterLocale={i18n.language === "cn" ? "zh-cn" : i18n.language}
              key={i18n.language} // dili değişince yeniden mount olur
            >
              <DemoContainer
                sx={{ alignItems: "center", width: "100%" }}
                components={["DatePicker", "DatePicker"]}
              >
                <Box width={"100%"}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.5,
                    }}
                  >
                    {/* <FormControl
                      fullWidth
                      sx={{
                        maxWidth: { xs: "100%" },
                        minWidth: "unset !important",
                        textTransform: 'capitalize',
                      }}
                    >
                      <InputLabel sx={{textTransform: 'capitalize'}} id="demo-simple-select-label">
                        Provider
                      </InputLabel>
                      <Select
                        name="provider"
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={formValues.provider}
                        label="provider"
                        onChange={handleChange}
                      >
                        {providers?.map((provider, idx) => {
                          return (
                            <MenuItem
                              key={provider.partner_id}
                              value={provider}
                            >
                              {provider.partner_name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl> */}

                    <FormControl fullWidth>
                      <InputLabel sx={{ textTransform: 'capitalize' }} id="demo-simple-select-label">
                        {t("provider")}
                      </InputLabel>
                      <Select
                        name="provider"
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        multiple
                        value={
                          Array.isArray(formValues.provider)
                            ? formValues.provider
                            : formValues.provider
                              ? [formValues.provider]
                              : []
                        }
                        label={t("provider")}
                        onChange={handleChange}
                        renderValue={(selected) => {
                          if (!Array.isArray(selected)) return "";
                          return selected
                            .map((item) => item.partner_name)
                            .join(", ");
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 224,
                              width: 250,
                            },
                          },
                        }}
                      >
                        {providers?.map((provider, idx) => {
                          const isSelected = Array.isArray(formValues.provider)
                            ? formValues.provider.some(
                              (p) => p.partner_id === provider.partner_id
                            )
                            : formValues.provider &&
                            formValues.provider.partner_id ===
                            provider.partner_id;

                          return (
                            <MenuItem
                              key={provider.partner_id}
                              value={provider}
                            >
                              <Checkbox checked={isSelected} />
                              <ListItemText primary={provider.partner_name} />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <FormControl
                      fullWidth
                    // disabled={!formValues.provider}
                    >
                      <InputLabel sx={{ textTransform: 'capitalize' }} id="demo-simple-select-label">
                        {t("residences")}
                      </InputLabel>
                      <Select
                        name="residences"
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={t("residences")}
                        multiple
                        value={
                          Array.isArray(formValues.residences)
                            ? formValues.residences
                            : formValues.residences
                              ? [formValues.residences]
                              : []
                        }
                        onChange={handleChange}
                        renderValue={(selected) => {
                          if (!Array.isArray(selected)) return "";
                          return selected
                            .map((item) => item.campus_name)
                            .join(", ");
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 224,
                              width: 250,
                            },
                          },
                        }}
                      >
                        {campuses?.map((campus, idx) => {
                          const isSelected = Array.isArray(
                            formValues.residences
                          )
                            ? formValues.residences.some(
                              (p) => p.campus_id === campus.campus_id
                            )
                            : formValues.residences &&
                            formValues.residences.campus_id ===
                            campus.campus_id;

                          return (
                            <MenuItem key={campus.campus_id} value={campus}>
                              <Checkbox checked={isSelected} />
                              <ListItemText primary={campus.campus_name} />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <FormControl
                      fullWidth
                    // disabled={!formValues.residences}
                    >
                      <InputLabel sx={{ textTransform: 'capitalize' }} id="demo-simple-select-label">
                        {t("roomTypes")}
                      </InputLabel>
                      <Select
                        name="course"
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={formValues.course}
                        label="Room Types"
                        onChange={handleChange}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 224,
                              width: 250,
                            },
                          },
                        }}
                      >
                        <MenuItem
                          value={courses?.length ? "All Room Types" : null}
                        >
                          {"All Room Types"}
                        </MenuItem>
                        {courses?.map((course, idx) => {
                          return (
                            <MenuItem key={course.course_id} value={course}>
                              <Box display="flex" flexDirection="column">
                                <Typography variant="body1" fontWeight={600}>
                                  {course.course_name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {course?.campus_name}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="universities-autocomplete"
                        options={universities || []}
                        getOptionLabel={(option) => option.university_title || ""}
                        isOptionEqualToValue={(option, value) => option.university_id === value.university_id}
                        value={
                          Array.isArray(formValues.universities)
                            ? formValues.universities
                            : formValues.universities
                            ? [formValues.universities]
                            : []
                        }
                        onChange={(event, newValue) => {
                          handleChange({
                            target: {
                              name: "universities",
                              value: newValue,
                            },
                          });
                        }}
                        // --- STİL DOKUNUŞU BURADA BAŞLIYOR ---
                        sx={{
                          "& .MuiChip-root": {
                            color: "#ffffff", // Yazı rengini beyaz yapar
                            backgroundColor: "rgba(255, 255, 255, 0.15)", // Hafif saydam beyaz arka plan
                            border: "1px solid rgba(255, 255, 255, 0.3)", // Şık bir çerçeve
                          },
                          "& .MuiChip-deleteIcon": {
                            color: "#ffffff !important", // Silme (X) ikonunu beyaz yapar
                            opacity: 0.8,
                          },
                          "& .MuiInputBase-input": {
                            color: "#ffffff", // Yazılan arama metnini beyaz yapar
                          },
                          "& .MuiInputLabel-root": {
                            color: "#ffffff", // "Institute" label'ını beyaz yapar
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(255, 255, 255, 0.5)", // Kenarlık rengini biraz yumuşatır
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ffffff", // Üzerine gelince kenarlığı parlatır
                          },
                        }}
                        // --- STİL DOKUNUŞU BURADA BİTİYOR ---
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("institute")}
                            variant="outlined"
                            placeholder="Search..."
                          />
                        )}
                      />
                    </FormControl>

                    <Box
                      sx={{
                        gridColumn: "1 / -1",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                        gap: 1.5,
                      }}
                    >
                      <DatePicker
                        className="check-in"
                        sx={{
                          maxWidth: { xs: "100%" },
                          minWidth: "unset !important",
                          textTransform: 'capitalize',
                          width: "100%",
                        }}
                        label={t("checkIn")}
                        value={checkIn}
                        onChange={(newValue) => setCheckIn(newValue)}
                        error={checkInError}
                        // minDate={dayjs()} // Bugünün tarihi, geçmiş tarihler seçilemez
                        maxDate={formValues?.checkOut || null}
                        shouldDisableDate={(date) =>
                          disableSameDate(date) || disableWeekdays(date)
                        }
                      />
                      <TextField
                        sx={{
                          maxWidth: { xs: "100%" },
                          minWidth: "unset !important",
                          textTransform: 'capitalize',
                          width: "100%",
                        }}
                        onChange={handleDurationChange}
                        name="duration"
                        id="duration"
                        value={durationWeeks}
                        label={`${t("duration")} (${t("weeks")})`}
                        variant="outlined"
                        type="number"
                        disabled={!checkIn}
                        inputProps={{ min: 1 }}
                      />
                      <DatePicker
                        className="check-out"
                        sx={{
                          maxWidth: { xs: "100%" },
                          minWidth: "unset !important",
                          textTransform: 'capitalize',
                          width: "100%",
                        }}
                        label={t("checkOut")}
                        error={checkOutError}
                        value={checkOut}
                        onChange={(newValue) => setCheckOut(newValue)}
                        minDate={formValues?.checkIn || null}
                        shouldDisableDate={(date) =>
                          disableSameDate(date) || disableWeekdays(date)
                        }
                      />
                    </Box>

                    <FormControl
                      fullWidth
                      sx={{
                        maxWidth: { xs: "100%" },
                        minWidth: "unset !important",
                        textTransform: 'capitalize',
                      }}
                    >
                      <InputLabel sx={{ textTransform: 'capitalize' }} id="demo-simple-select-label">
                        {t("occupancy")}
                      </InputLabel>
                      <Select
                        name="occupancy"
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={formValues.occupancy}
                        label={t("occupancy")}
                        onChange={handleChange}
                      >
                        <MenuItem value={"single"}>{t("single")}</MenuItem>
                        <MenuItem value={"dual"}>{t("dual")}</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      sx={{
                        maxWidth: { xs: "100%" },
                        minWidth: "unset !important",
                        textTransform: 'capitalize',
                        width: "100%",

                      }}
                      onChange={handleChange}
                      name="numberOfStudents"
                      id="numberOfStudents"
                      value={formValues.numberOfStudents}
                      label={t("numberOfStudents")}
                      variant="outlined"
                      type="number"
                    />
                  </Box>
                  <TextField
                    multiline
                    rows={4}
                    maxRows={8}
                    sx={{
                      mt: 3,
                      maxWidth: { xs: "100%" },
                      minWidth: "unset !important",
                      textTransform: 'capitalize',
                      width: "100%",
                    }}
                    onChange={handleChange}
                    name="additionalNote"
                    value={formValues.additionalNote}
                    id="outlined-basic"
                    label={t("additionalNote")}
                    variant="outlined"
                    type="text"
                  />
                </Box>
              </DemoContainer>
            </LocalizationProvider>
          </Box>
        </Box>

        {/* Butonlar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mt: 3,
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setFormValues(initialState);
              setAvailableRooms([]);
              setOrderType("low-to-high");
              setSelectedRooms([]);
              navigate("/room-finder");
              setRoomControl(true);
            }}
            sx={{
              backgroundColor: "#e63946 !important",
              color: "#ffffff !important",
              "&:hover": {
                backgroundColor: "#c1121f !important",
              },
            }}
          >
            {t("clearFilters")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFindRooms}
            disabled={!formValues.checkIn || !formValues.checkOut}
          >
            {t("findRooms")}
          </Button>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
